import React, { useState, useEffect } from 'react';

const MESES_NOMBRES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const IconCalendar = () => (
    <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const IconChevronDown = () => (
    <svg className="w-3.5 h-3.5 text-slate-500 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const DateRangePickerFinanzas = ({ 
    fechaDesde, 
    setFechaDesde, 
    fechaHasta, 
    setFechaHasta, 
    selectedMonth, 
    setSelectedMonth, 
    selectedYear, 
    setSelectedYear, 
    onSubmit 
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [modoBusqueda, setModoBusqueda] = useState('PERIODO'); // 'PERIODO' | 'RANGO'

    // Formateador interno YYYY-MM-DD
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Al cambiar mes o año en modo PERIODO, ajusta automáticamente fechaDesde y fechaHasta al primer y último día del mes
    const handleMonthYearChange = (mes, anio) => {
        setSelectedMonth(mes);
        setSelectedYear(anio);
        setModoBusqueda('PERIODO');

        const mStr = String(mes).padStart(2, '0');
        const desde = `${anio}-${mStr}-01`;
        const ultimoDia = new Date(anio, mes, 0).getDate();
        const hasta = `${anio}-${mStr}-${String(ultimoDia).padStart(2, '0')}`;

        setFechaDesde(desde);
        setFechaHasta(hasta);
    };

    // Aplicar Atajos Rápidos
    const aplicarPreset = (tipo) => {
        const hoy = new Date();
        const anioActual = hoy.getFullYear();
        const mesActual = hoy.getMonth() + 1;

        if (tipo === 'MES_ACTUAL') {
            handleMonthYearChange(mesActual, anioActual);
        } else if (tipo === 'MES_ANTERIOR') {
            const mesAnt = mesActual === 1 ? 12 : mesActual - 1;
            const anioAnt = mesActual === 1 ? anioActual - 1 : anioActual;
            handleMonthYearChange(mesAnt, anioAnt);
        } else if (tipo === 'TRIMESTRE') {
            setModoBusqueda('RANGO');
            const tresMesesAtras = new Date(hoy.getFullYear(), hoy.getMonth() - 3, 1);
            setFechaDesde(formatDate(tresMesesAtras));
            setFechaHasta(formatDate(hoy));
        } else if (tipo === 'ESTE_ANO') {
            setModoBusqueda('RANGO');
            const inicioAnio = new Date(anioActual, 0, 1);
            setFechaDesde(formatDate(inicioAnio));
            setFechaHasta(formatDate(hoy));
        }
        setDropdownOpen(false);
    };

    useEffect(() => {
        if (!dropdownOpen) return;
        const closeMenu = () => setDropdownOpen(false);
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [dropdownOpen]);

    return (
        <div className="flex items-center gap-3">
            <form onSubmit={onSubmit} className="flex items-center gap-2.5 bg-white p-1.5 pl-3 border border-slate-200/80 rounded-2xl shadow-sm hover:border-slate-300 transition-all">
                
                {/* SELECTOR RÁPIDO DE MES Y AÑO */}
                <div className="flex items-center gap-2">
                    <IconCalendar />
                    <div className="flex items-center gap-1.5">
                        <select
                            value={selectedMonth}
                            onChange={(e) => handleMonthYearChange(Number(e.target.value), selectedYear)}
                            className="bg-slate-50 border border-slate-200 rounded-xl px-2 py-1 text-xs font-bold text-slate-800 outline-none cursor-pointer focus:bg-white focus:border-[#01032d]"
                        >
                            {MESES_NOMBRES.map((mes, idx) => (
                                <option key={mes} value={idx + 1}>{mes}</option>
                            ))}
                        </select>

                        <select
                            value={selectedYear}
                            onChange={(e) => handleMonthYearChange(selectedMonth, Number(e.target.value))}
                            className="bg-slate-50 border border-slate-200 rounded-xl px-2 py-1 text-xs font-mono font-bold text-slate-800 outline-none cursor-pointer focus:bg-white focus:border-[#01032d]"
                        >
                            {[2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="h-6 w-px bg-slate-200"></div>

                {/* RANGO DESDE / HASTA LIBRE */}
                <div className="flex items-center gap-2">
                    <div className="flex flex-col text-left">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider leading-none">Emisión / Gasto</span>
                        <input 
                            type="date" 
                            value={fechaDesde} 
                            onChange={(e) => {
                                setFechaDesde(e.target.value);
                                setModoBusqueda('RANGO');
                            }} 
                            className="bg-transparent border-none p-0.5 text-xs font-bold font-mono text-slate-700 outline-none cursor-pointer mt-0.5" 
                            required 
                        />
                    </div>
                    <span className="text-slate-300 text-xs font-bold">-</span>
                    <div className="flex flex-col text-left">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider leading-none">Hasta</span>
                        <input 
                            type="date" 
                            value={fechaHasta} 
                            onChange={(e) => {
                                setFechaHasta(e.target.value);
                                setModoBusqueda('RANGO');
                            }} 
                            className="bg-transparent border-none p-0.5 text-xs font-bold font-mono text-slate-700 outline-none cursor-pointer mt-0.5" 
                            required 
                        />
                    </div>
                </div>

                <button 
                    type="submit" 
                    className="py-2 px-4 bg-[#01032d] hover:bg-[#0c0e3e] text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-md active:scale-95"
                >
                    Filtrar
                </button>
            </form>

            {/* MENÚ DE ATAJOS RÁPIDOS */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/80 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm active:scale-95"
                >
                    <span>{modoBusqueda === 'PERIODO' ? `${MESES_NOMBRES[selectedMonth - 1]} ${selectedYear}` : 'Rango Personalizado'}</span>
                    <IconChevronDown />
                </button>

                {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200/85 rounded-2xl shadow-xl z-50 p-1.5 animate-scale-up">
                        <div className="px-3 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">
                            Períodos Rápidos
                        </div>
                        <button type="button" onClick={() => aplicarPreset('MES_ACTUAL')} className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex justify-between">
                            <span>Mes Actual</span>
                            <span className="text-[10px] font-mono text-slate-400">Mes</span>
                        </button>
                        <button type="button" onClick={() => aplicarPreset('MES_ANTERIOR')} className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex justify-between">
                            <span>Mes Anterior</span>
                            <span className="text-[10px] font-mono text-slate-400">-1M</span>
                        </button>
                        <button type="button" onClick={() => aplicarPreset('TRIMESTRE')} className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex justify-between">
                            <span>Último Trimestre</span>
                            <span className="text-[10px] font-mono text-slate-400">90d</span>
                        </button>
                        <button type="button" onClick={() => aplicarPreset('ESTE_ANO')} className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex justify-between">
                            <span>Este Año</span>
                            <span className="text-[10px] font-mono text-slate-400">YTD</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DateRangePickerFinanzas;